import React from "react";
import strings from "../../core/translate";
import "../../assets/css/worker-form.css";
import {
  FormSection,
  FormGrid,
  TextField,
  SelectField,
  SwitchField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";

// Owner, Manager, Mechanic — what each may reach is spelled out under the
// choice, because it decides where the person can sign in.
export const WORKER_ROLES = ["owner", "manager", "mechanic"];

/** A row of mutually exclusive chips — a short list read at a glance. */
const ChipChoice = ({ label, help, value, options, onChange, error }) => (
  <div className="mfield">
    <label className="mfield__label">{label}</label>
    <div className="worker-chips" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          role="radio"
          aria-checked={value === option.value}
          className={`worker-chip${value === option.value ? " is-on" : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
    {error ? (
      <span className="mfield__error">{error}</span>
    ) : (
      help && <span className="mfield__help">{help}</span>
    )}
  </div>
);

/**
 * Worker details. When the form is opened from a garage the branch is already
 * known, so the picker is left out and the garage is shown instead.
 */
const MerchantWorkerForm = ({
  name,
  title,
  phoneNumber,
  username,
  password,
  selectedBranch,
  branches,
  branchName,
  branchFixed,
  canAccessApp,
  role,
  tierId,
  tiers = [],
  isEdit,
  loading,
  error,
  errors = {},
  handleValidSubmit,
  handleFieldChange,
  handleBranchSelect,
  onCancel,
}) => (
  <form className="mform" onSubmit={handleValidSubmit}>
    <FormSection title={strings.information} icon="nc-icon nc-single-02">
      <FormGrid>
        <TextField
          label={strings.name}
          required
          value={name}
          onChange={(value) => handleFieldChange("name", value)}
          error={errors.name}
        />
        <TextField
          label={strings.occupation}
          required
          value={title}
          onChange={(value) => handleFieldChange("title", value)}
          error={errors.title}
        />
      </FormGrid>

      <FormGrid>
        <TextField
          label={strings.phonenumber || strings.phone}
          required
          value={phoneNumber}
          onChange={(value) => handleFieldChange("phoneNumber", value)}
          error={errors.phoneNumber}
        />
        <TextField
          label={isEdit ? strings.new_password : strings.password}
          type="password"
          required={!isEdit}
          value={password}
          onChange={(value) => handleFieldChange("password", value)}
          error={errors.password}
          help={
            isEdit ? strings.password_change_hint : strings.worker_password_hint
          }
        />
      </FormGrid>

      <FormGrid full>
        <TextField
          label={strings.login_username}
          value={username}
          onChange={(value) => handleFieldChange("username", value)}
          error={errors.username}
          help={strings.worker_username_hint}
        />
      </FormGrid>

      {branchFixed ? (
        <FormGrid full>
          <div className="mfield">
            <label className="mfield__label">{strings.garage}</label>
            <div className="mfield__static">
              <i className="nc-icon nc-bank" />
              {branchName || strings.garage}
            </div>
            <span className="mfield__help">{strings.worker_garage_hint}</span>
          </div>
        </FormGrid>
      ) : (
        <FormGrid full>
          <SelectField
            label={strings.garage}
            required
            value={selectedBranch}
            options={branches}
            onChange={handleBranchSelect}
            error={errors.selectedBranch}
          />
        </FormGrid>
      )}

    </FormSection>

    <FormSection title={strings.role_and_tier} icon="nc-icon nc-badge">
      <FormGrid full>
        <ChipChoice
          label={strings.staff_role}
          value={role}
          options={WORKER_ROLES.map((value) => ({
            value,
            label: strings[`role_${value}`],
          }))}
          help={strings[`role_${role}_hint`]}
          onChange={(value) => handleFieldChange("role", value)}
        />
      </FormGrid>

      <FormGrid full>
        <ChipChoice
          label={strings.tier}
          value={tierId || ""}
          options={[
            { value: "", label: strings.no_tier },
            ...tiers.map((tier) => ({ value: tier.id, label: tier.name })),
          ]}
          help={tiers.length === 0 ? strings.no_tiers_for_staff_hint : ""}
          onChange={(value) => handleFieldChange("tierId", value)}
        />
      </FormGrid>

      <FormGrid full>
        <SwitchField
          label={strings.access_app}
          help={strings.access_app_hint}
          checked={canAccessApp}
          onChange={(value) => handleFieldChange("canAccessApp", value)}
        />
      </FormGrid>
    </FormSection>

    <FormAlert>{error}</FormAlert>
    <FormActions onCancel={onCancel} loading={loading} />
  </form>
);

export default MerchantWorkerForm;
